import { useEffect, useRef, useState } from 'react'
import { notifications } from '@mantine/notifications'
import { useEvidenciaUpload } from '../../hooks/useEvidencias'

interface Props {
  open: boolean
  onClose: () => void
  ordemServicoId: string
  /** Identificador legível do que está sendo assinado (ex.: 'Conclusão checklist 23') */
  contexto: string
  /** Callback chamado após upload da assinatura como evidência. */
  onAssinado?: (evidenciaId: string) => void
}

const CANVAS_W = 480
const CANVAS_H = 200

/**
 * Modal de captura de assinatura digital via canvas HTML5.
 * Sem libs externas — pointer events nativos para mouse/touch.
 * Salva como PNG dataURL → upload via /api/v1/evidencias/ tipo='laudo'.
 */
export default function AssinaturaModal({
  open,
  onClose,
  ordemServicoId,
  contexto,
  onAssinado,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawingRef = useRef(false)
  const lastPointRef = useRef<{ x: number; y: number } | null>(null)
  const [tocou, setTocou] = useState(false)
  const upload = useEvidenciaUpload()

  // Limpa canvas quando abre
  useEffect(() => {
    if (!open) return
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.fillStyle = 'white'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.strokeStyle = '#1565C0'
    ctx.lineWidth = 2.5
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    setTocou(false)
  }, [open])

  function pegarPos(e: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } {
    const canvas = canvasRef.current!
    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    }
  }

  function onPointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    e.preventDefault()
    drawingRef.current = true
    lastPointRef.current = pegarPos(e)
    canvasRef.current?.setPointerCapture(e.pointerId)
    setTocou(true)
  }

  function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current) return
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx || !lastPointRef.current) return
    const pos = pegarPos(e)
    ctx.beginPath()
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y)
    ctx.lineTo(pos.x, pos.y)
    ctx.stroke()
    lastPointRef.current = pos
  }

  function onPointerUp(e: React.PointerEvent<HTMLCanvasElement>) {
    drawingRef.current = false
    lastPointRef.current = null
    canvasRef.current?.releasePointerCapture(e.pointerId)
  }

  function handleLimpar() {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    ctx.fillStyle = 'white'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    setTocou(false)
  }

  async function handleSalvar() {
    const canvas = canvasRef.current
    if (!canvas || !tocou) {
      notifications.show({ color: 'orange', message: 'Assine antes de salvar.' })
      return
    }
    canvas.toBlob(async (blob) => {
      if (!blob) {
        notifications.show({ color: 'red', message: 'Falha ao gerar imagem da assinatura.' })
        return
      }
      const file = new File([blob], `assinatura-${Date.now()}.png`, { type: 'image/png' })
      try {
        const ev = await upload.mutateAsync({
          ordemServicoId,
          arquivo: file,
          tipo: 'laudo',
          descricao: `Assinatura digital — ${contexto}`,
        })
        notifications.show({ color: 'green', message: 'Assinatura registrada.' })
        onAssinado?.(ev.id)
        onClose()
      } catch (err) {
        const msg =
          (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
          'Erro ao enviar assinatura.'
        notifications.show({ color: 'red', title: 'Falha', message: msg })
      }
    }, 'image/png')
  }

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Assinatura digital"
      data-testid="assinatura-modal"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          maxWidth: '520px',
          width: '90%',
        }}
      >
        <h2 style={{ marginTop: 0, fontSize: '1.1rem' }}>Assinatura digital</h2>
        <p style={{ margin: '0.5rem 0', color: '#666', fontSize: '0.85rem' }}>
          {contexto}
        </p>

        <div
          style={{
            border: '2px dashed #1565C0',
            borderRadius: '4px',
            background: '#FAFAFA',
            touchAction: 'none',
            padding: '4px',
          }}
        >
          <canvas
            ref={canvasRef}
            width={CANVAS_W}
            height={CANVAS_H}
            data-testid="assinatura-canvas"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            style={{
              width: '100%',
              maxWidth: `${CANVAS_W}px`,
              height: 'auto',
              display: 'block',
              cursor: 'crosshair',
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button
            type="button"
            onClick={handleLimpar}
            disabled={!tocou || upload.isPending}
            data-testid="assinatura-limpar"
            style={{
              padding: '0.5rem 1rem',
              background: 'none',
              border: '1px solid #888',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Limpar
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={upload.isPending}
            style={{
              padding: '0.5rem 1rem',
              background: 'none',
              border: '1px solid #888',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSalvar}
            disabled={!tocou || upload.isPending}
            data-testid="assinatura-salvar"
            style={{
              padding: '0.5rem 1rem',
              background: '#1565C0',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            {upload.isPending ? 'Salvando...' : 'Confirmar assinatura'}
          </button>
        </div>
      </div>
    </div>
  )
}
